/**
 * PersonaSplitTile.js — Phase D · Command Centre admin widget.
 * Renders the live adult-vs-teen activity + revenue split returned by
 * GET /api/admin/persona-metrics. Drops straight into the Command
 * Centre user-metrics panel.
 */
import React, { useEffect, useState } from 'react';
import { GraduationCap, Briefcase, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const PersonaSplitTile = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.request('/api/admin/persona-metrics')
      .then(setData)
      .catch((e) => setError(e?.message || 'Failed to load'));
  }, []);

  if (error) {
    return (
      <div className="rounded-2xl bg-white border border-rose-100 p-5 text-sm text-rose-600" data-testid="persona-split-tile-error">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 p-5 flex items-center justify-center" data-testid="persona-split-tile-loading">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }

  const teens = data.active_teens || {};
  const adults = data.active_adults || {};
  const totalCount = (teens.count || 0) + (adults.count || 0);

  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-4 sm:p-6 shadow-sm min-w-0" data-testid="persona-split-tile">
      <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4">Audience Split</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4 min-w-0" data-testid="persona-split-teens">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap size={16} className="text-indigo-600 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-widest text-indigo-700 truncate">Active Teens</span>
          </div>
          <p className="text-3xl font-black text-indigo-900">{teens.count?.toLocaleString() ?? 0}</p>
          <p className="text-xs text-indigo-600 mt-1 truncate">MRR {fmtMoney(teens.mrr_cents)} · ARPU {fmtMoney(teens.arpu_cents)}</p>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 min-w-0" data-testid="persona-split-adults">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase size={16} className="text-amber-700 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-widest text-amber-700 truncate">Active Adults</span>
          </div>
          <p className="text-3xl font-black text-amber-900">{adults.count?.toLocaleString() ?? 0}</p>
          <p className="text-xs text-amber-700 mt-1 truncate">MRR {fmtMoney(adults.mrr_cents)} · ARPU {fmtMoney(adults.arpu_cents)}</p>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500 gap-2 flex-wrap">
        <span className="truncate min-w-0">{totalCount.toLocaleString()} total active subscribers</span>
        <span className="font-mono font-bold text-gray-900 whitespace-nowrap">Total MRR {fmtMoney(data.total_mrr_cents)}</span>
      </div>
    </div>
  );
};

export default PersonaSplitTile;
